import React from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { Moon, Sun } from 'lucide-react';
import { useDesignTheme } from '../../hooks/useDesignTheme';
import { ColorModeContext } from '../../app/theme';
import { ParticleBackground } from './ParticleBackground';
import { FloatingCode } from './FloatingCode';

export default function AppLayout() {
  const { theme, isDark } = useDesignTheme();
  const { toggle } = React.useContext(ColorModeContext);
  const location = useLocation();
  
  // Show floating code only on home page or where appropriate
  const showFloatingCode = location.pathname === '/';

  const clickCountRef = React.useRef(0);
  const firstClickTimeRef = React.useRef(0);

  const handleLogoClick = (e: React.MouseEvent) => {
    const now = Date.now();
    
    if (clickCountRef.current === 0) {
      firstClickTimeRef.current = now;
      clickCountRef.current = 1;
    } else {
      if (now - firstClickTimeRef.current > 3000) {
        // Reset if time window exceeded
        firstClickTimeRef.current = now;
        clickCountRef.current = 1;
      } else {
        clickCountRef.current += 1;
      }
    }

    if (clickCountRef.current >= 7) {
      e.preventDefault();
      localStorage.setItem('odc_admin_access', 'true');
      window.open('/admin', '_blank');
      clickCountRef.current = 0;
      firstClickTimeRef.current = 0;
    }
  };

  return (
    <div 
      className="min-h-screen relative overflow-hidden transition-colors duration-300 font-sans"
      style={{ backgroundColor: theme.bg, color: theme.text }}
    >
      {/* Particle canvas */}
      <ParticleBackground />

      {/* Floating code snippets */}
      {showFloatingCode && <FloatingCode />}

      {/* Decorative elements */}
      <div 
        className="absolute top-20 right-20 w-64 h-64 rounded-full blur-3xl opacity-40 pointer-events-none"
        style={{ backgroundColor: theme.accent1 }}
      />
      <div 
        className="absolute bottom-20 left-20 w-96 h-96 rounded-full blur-3xl opacity-30 pointer-events-none"
        style={{ backgroundColor: theme.accent2 }}
      />
      
      {/* Top bar with logo, navigation and theme toggle */}
      <div className="absolute top-0 left-0 right-0 z-20 px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between">
          {/* Brand logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2 group no-underline"
            onClick={handleLogoClick}
          >
            <div 
              className="w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300 group-hover:scale-110"
              style={{ 
                backgroundColor: theme.text,
                color: theme.bg 
              }}
            >
              <span className="text-lg font-bold">DO</span>
            </div>
            <span 
              className="hidden sm:block text-lg transition-colors font-medium"
              style={{ color: theme.text }}
            >
              Dan Ogbuja
            </span>
          </Link>

          <div className="flex items-center gap-4 sm:gap-6">
            {/* Navigation */}
            <nav className="flex items-center gap-3 sm:gap-6 text-sm font-medium">
              <Link 
                to="/projects" 
                className="transition-colors hover:opacity-70 no-underline"
                style={{ color: theme.textSecondary }}
              >
                Projects
              </Link>
              <Link 
                to="/blog" 
                className="transition-colors hover:opacity-70 no-underline"
                style={{ color: theme.textSecondary }}
              >
                Blog
              </Link>
              <Link 
                to="/contact" 
                className="transition-colors hover:opacity-70 no-underline"
                style={{ color: theme.textSecondary }}
              >
                Contact
              </Link>
            </nav>

            {/* Theme toggle */}
            <button
              onClick={toggle}
              className="p-2 rounded-lg transition-all duration-300 hover:scale-110 border-none cursor-pointer flex items-center justify-center"
              style={{ 
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(42, 42, 42, 0.1)',
                color: theme.text
              }}
              aria-label="Toggle theme"
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10">
        <Outlet />
      </div>
    </div>
  );
}
